import { useState } from 'react'
import { fmtDisplayDateTime } from '@utils/displayDate'
import reportService from '@api/v1/reportService'
import { responseStatus } from '@api/v1/upstreamStatus'
import { useAuth0 } from '@auth0/auth0-react'
import usePreferencesStore from '@store/preferencesStore'
import PopoverMenu from '@components/common/PopoverMenu'
import { exportClientCsv, exportClientExcel } from './exportClientView'
import { exportClientPdf } from './exportClientPdf'
import { clientExportRefusal } from './clientExportRefusal'
import { exportFileName } from './reportGridUtils'
import { DownloadIcon, ChevronDownIcon } from './queue/queueIcons'
import { fmtRowCount } from './queue/queueFormat'
import { getTenant } from '@config/tenant'
import { trackExport } from '@/analytics/pendo'

/**
 * Export control (CC-49370). Default = the authoritative SERVER export
 * (CSV/XLSX streamed from cb-api, full dataset, server formatting). Two scope
 * checkboxes let the user instead export EXACTLY what they see in the grid:
 *   ☐ Apply active column filters
 *   ☐ Only visible columns, current order
 * When either is checked, CSV/Excel build client-side from the grid's current
 * view (`getView()`); when both are unchecked the server export is unchanged.
 * Print always prints the on-screen grid (already the visible/filtered view).
 * PDF (CC-49594) is ALWAYS client-built from `getView()` — there is no server
 * PDF export — so it still honors the scope checkboxes when checked and
 * exports the full view when not. It's only offered when a universal grid
 * publishes `getView`.
 *
 * `getView` is provided only when a universal grid is mounted; bespoke reports
 * without one fall back to server-only export (no checkboxes).
 *
 * CC-49844: SPECIALIZED reports reach this component through
 * SpecializedReportShell, which owns its own per-report export pipeline (each
 * page passes a `download(format, getToken)`). They supply that as
 * `serverExport` so the authoritative default stays the page's own server
 * export while the scope options above become available everywhere. Without
 * this prop the generic `reportId` pipeline is used, exactly as before.
 * `exportFormats` lets a page hide a format its backend can't produce.
 */
/*
 * spec 094 — where the export events are emitted, and why NOT one level down.
 *
 * The tracking plan lists five "all distinct" export call sites, three of which
 * are `<service>.download()`. They are not distinct actions: on a specialized
 * page `SpecializedReportShell` passes its `download` in here as `serverExport`
 * (SpecializedReportShell.jsx:254), so one click on "Excel (.xlsx)" runs
 * handleExcel -> serverDownload -> serverExport -> the page's own
 * `<service>.download`. Instrumenting both layers would emit TWO Report Export
 * events for one user action and double the export count on exactly the 21
 * specialized reports the beta most wants to measure.
 *
 * So the events are emitted HERE, at the user action, and the transports are
 * left alone. That also covers, for free, the three transports the plan missed
 * entirely — accountingReportService (5 methods), apReportService, and the
 * shell's own onClientExport fallback — because every one of them is reached
 * only through this menu.
 *
 * `exportPath` records which of the three routes ran, because `engine` alone
 * cannot tell the truth here: ARReport's `download` prop is itself a CLIENT
 * builder (ARReport.jsx:685), as is the shell's onClientExport fallback, so
 * "delegated to the page" does not imply "built on the server". `engine` means
 * strictly "did THIS component build the file from the grid view".
 */
const ReportExport = ({ reportId, filters = [], processId, rowCount = 0, getView, reportName, analyticsName, reportSummary, filtersSummary, firmName = '', gridFiltered = false, hasGroups = false, serverExport, exportFormats = ['csv', 'xlsx'], surface = 'report_view' }) => {
  const { getAccessTokenSilently, user } = useAuth0()
  const prefs = usePreferencesStore()
  const [honorColumns, setHonorColumns] = useState(false)
  const [honorFilters, setHonorFilters] = useState(false)
  // CC-49843: grouped reports offer a "summary only" PDF — group names +
  // server subtotals + grand total, no leaf rows (the picky-presentation ask).
  const [pdfSummaryOnly, setPdfSummaryOnly] = useState(false)
  // CC-49844: header-less "data only" Excel — skip the title/filters/generated
  // rows so the file drops straight into another system. Persisted per user
  // (preferencesStore). Checking it forces the CLIENT build (the server export
  // stays authoritative and untouched — it keeps its own header format).
  const dataOnly = !!prefs.getExportDataOnly?.()
  const setDataOnly = (on) => prefs.setExportDataOnly?.(on)
  const scoped = (honorColumns || honorFilters || dataOnly) && typeof getView === 'function'

  // CC-49852: client-built filenames follow V1's "{ts}_{Report_Name}.{ext}"
  // convention (exportFileName); the default server export keeps the server's
  // own content-disposition name (already V1's).
  const clientName = reportName || `report-${reportId}`

  const serverDownload = async (format) => {
    // CC-49844: a specialized page owns its export endpoint — defer to it so
    // the unchecked-boxes default stays that page's authoritative server export.
    if (typeof serverExport === 'function') {
      try {
        await serverExport(format)
      } catch (err) {
        console.error('Export failed', err)
        alert(`Export failed: ${err.message}`)
      }
      return
    }
    if (!reportId) return
    const base = reportService.exportUrl(reportId, format, { filters, processId, rowCount })
    // Spec 142: the server stamps the file "Generated <time> <zone>". Send the browser's
    // own IANA zone so that time reads as the user's; cb-api validates it and states UTC
    // without one. Read only by cb-api — never forwarded to CB (`timeZoneId` is CB's).
    let tz = ''
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { tz = '' }
    const url = tz ? `${base}${base.includes('?') ? '&' : '?'}tz=${encodeURIComponent(tz)}` : base
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) throw new Error(`Export failed: ${responseStatus(resp)}`)
      const blob = await resp.blob()
      const dispo = resp.headers.get('content-disposition') || ''
      const m = /filename="?([^"]+)"?/.exec(dispo)
      const filename = m?.[1] || `report-${reportId}.${format}`
      const objUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objUrl)
    } catch (err) {
      console.error('Export failed', err)
      alert(`Export failed: ${err.message}`)
    }
  }

  /**
   * CC-50004: what the screen says, the file says.
   *
   * The server export is still the authoritative default, but it formats
   * server-side and cannot know about a client-side display choice. So when the
   * user has made an EXPLICIT "Show $ on amounts" choice, the export switches to
   * the client build — the only path that can honor it. Auto (no explicit choice)
   * keeps the server export exactly as before.
   *
   * The alternative would have been to leave the toggle's promise scoped to the
   * screen. Jordan's call was that the UI is authoritative: if it's checked the
   * export shows the symbol, if it isn't it doesn't.
   */
  const viewHonorsCurrency = (view) => view?.currencyOverride != null

  /**
   * One Report Export event per user action. `clientBuilt` is the branch this
   * component actually took, not the button pressed — the product question is
   * whether users trust the authoritative server export or prefer "export what
   * you see", and a mislabelled engine answers it backwards.
   */
  const emitExport = (format, clientBuilt, extra) => {
    trackExport({
      reportId,
      // Spec 209 §3: a page whose name is firm-authored (an Advanced Report) names
      // itself generically to analytics; the file and the screen keep the real name.
      reportName: analyticsName ?? reportName,
      format,
      engine: clientBuilt ? 'client' : 'server',
      exportPath: clientBuilt
        ? 'client_view'
        : (typeof serverExport === 'function' ? 'page_export' : 'generic_server'),
      surface,
      dataOnly,
      honorColumns,
      honorFilters,
      // The grid is filtered but the export is not scoped to it — the exact
      // mismatch two testers independently filed as a bug (CC-50021/50028).
      gridFiltered,
      rowCount,
      ...extra,
    })
  }

  /**
   * Spec 157 (CC-53921): the page may refuse a file built from its view when it
   * cannot vouch for the rows in it (clientExportRefusal.js — only the General
   * Ledger does). Said the way every other export failure here is said. Never
   * consulted for the server export.
   */
  const refusedClientBuild = (view, format) => {
    const why = clientExportRefusal(view, format)
    if (why) alert(`Export failed: ${why}`)
    return !!why
  }

  const handleCsv = () => {
    const view = typeof getView === 'function' ? getView() : null
    const clientBuilt = scoped || viewHonorsCurrency(view)
    emitExport('csv', clientBuilt)
    if (clientBuilt) {
      if (refusedClientBuild(view, 'csv')) return
      // CC-50029: the client CSV carries the same audit header block as the
      // client Excel (and as cb-api's server-CSV stamp) unless "Data only".
      exportClientCsv(view, { honorColumns, honorFilters }, exportFileName(clientName, 'csv', { view: true }), dataOnly ? {} : {
        title: reportName,
        subtitle: reportSummary,
        filtersLine: filtersSummary ? `Filters — ${filtersSummary}` : undefined,
        generatedLine: `Generated ${fmtDisplayDateTime(new Date())}`,
      })
    } else serverDownload('csv')
  }
  const handleExcel = async () => {
    const currencyView = typeof getView === 'function' ? getView() : null
    const clientBuilt = scoped || viewHonorsCurrency(currencyView)
    emitExport('xlsx', clientBuilt)
    if (clientBuilt) {
      if (refusedClientBuild(currencyView, 'xlsx')) return
      try {
        // CC-49844: data-only drops the whole header block — the builder's
        // meta guards no-op on an empty object, leaving header row + data
        // (+ SUM-only totals) only.
        await exportClientExcel(currencyView, { honorColumns, honorFilters }, exportFileName(clientName, 'xlsx', { view: true }), dataOnly ? {} : {
          title: reportName,
          subtitle: reportSummary,
          // CC-49325/49326: the applied criteria (date range + basis) in the header.
          filtersLine: filtersSummary ? `Filters — ${filtersSummary}` : undefined,
          // CC-49340: run timestamp so exported financials are audit-ready.
          generatedLine: `Generated ${fmtDisplayDateTime(new Date())}`,
        })
      } catch (err) {
        console.error('Excel export failed', err)
        alert(`Export failed: ${err.message}`)
      }
    } else serverDownload('xlsx')
  }
  const handlePdf = async () => {
    // No server PDF exists: always build client-side from the grid's view.
    if (typeof getView !== 'function') return
    emitExport('pdf', true, { summaryOnly: !!(hasGroups && pdfSummaryOnly) })
    try {
      // CC-49844: "Data only" strips the masthead from the PDF too. CSV and Excel
      // gated their meta block on `dataOnly` from the start; PDF did not, so a
      // user who checked the box still got CENTERBASE DEMO / report title /
      // "Generated … · Printed by …" on page 1 (QA, Balance Sheet 34).
      //
      // Deliberate scope of "data only" on a PAGINATED format: the masthead and
      // the running head/foot TEXT go (they're branding + provenance), but
      // "Page X of Y" STAYS. Page numbers are structural — a multi-page table
      // with no pagination marker is unusable, and they carry no tenant, user,
      // or timestamp information, which is what "data only" is actually about.
      // exportClientPdf's didDrawPage already guards each running-foot line on
      // its meta key, so passing {} leaves page numbers as the only footer.
      const view = getView()
      if (refusedClientBuild(view, 'pdf')) return
      await exportClientPdf(view, { honorColumns, honorFilters, summaryOnly: hasGroups && pdfSummaryOnly }, exportFileName(clientName, 'pdf'), dataOnly ? {} : {
        // CC-49981: the firm's real name from system settings; the caller
        // resolves it once per page and falls back to the tenant when unset.
        firmLine: firmName || getTenant(),
        title: reportName,
        // No subtitle: Jonathan 7/8 — cut the report-summary text from PDFs.
        // CC-49325/49326: the applied criteria (date range + basis) in the header.
        filtersLine: filtersSummary ? `Filters applied: ${filtersSummary}` : undefined,
        // CC-49340: run timestamp so exported financials are audit-ready.
        // CC-49844: "Printed by" came from SpecializedReportShell's own PDF
        // builder; keeping it here preserves that audit line on the specialized
        // financial statements AND adds it to the generic path, so one masthead
        // now serves both.
        generatedLine: `Generated ${fmtDisplayDateTime(new Date())}${user?.name ? ` · Printed by ${user.name}` : ''}`,
      })
    } catch (err) {
      console.error('PDF export failed', err)
      alert(`Export failed: ${err.message}`)
    }
  }

  // CC-49844: a specialized page has no generic `reportId` — it exports through
  // `serverExport`. Enable whenever SOME export path exists (server pipeline or a
  // published grid view), so the menu isn't dead on those pages.
  const canExport = !!reportId || typeof serverExport === 'function' || typeof getView === 'function'
  const canScope = typeof getView === 'function'

  /*
   * spec 092 — ONE Export button, per Figma 110:11861.
   *
   * This was four always-visible buttons, a status line, four loose checkboxes and
   * a conditional warning: a right-aligned stack five rows deep on every report.
   * Moving that block into Bar 1 without collapsing it first is what made the
   * top-right of the page worse rather than better; collapsing it is the fix.
   *
   * The designer's note on this menu: "The four buttons and the loose checkboxes
   * are now one menu, since they all do the same job. Filters are honoured by
   * default and the row count shows what is coming out, so users know what they
   * are getting before they click."
   *
   * TWO DELIBERATE DEPARTURES FROM THE DRAWING, both following from the decision
   * to HOLD the export defaults:
   *
   *  1. The scope boxes start UNCHECKED, and the mock's "Recommended — exports
   *     what you see" caption is not reproduced. The mock ships them checked,
   *     which reverses the decision recorded above after CC-50021/CC-50028 — the
   *     server export is authoritative because two export paths that disagree on
   *     numbers is a correctness risk in a billing app. With server-authoritative
   *     defaults, that caption would point at the wrong option.
   *  2. Print sits below its own divider: it downloads nothing, so it does not
   *     belong under a DOWNLOAD heading.
   *
   * The footer keeps the designer's row count — the part that actually answers
   * those two bug reports, because it says what is coming out before you click —
   * and names the default out loud instead of implying it.
   *
   * The PDF-summary box is absent from the mock because the mock was drawn on an
   * UNGROUPED report, where `hasGroups` is false and it correctly does not render.
   * 86 of 140 reports are grouped, so it is the box most reports actually show
   * (CC-49843).
   */
  const formatItem = (labelText, onSelect, pendoName) => (
    <button
      type="button"
      role="menuitem"
      disabled={!canExport}
      // spec 094 — stable Pendo Feature selector. An attribute only: no change
      // to type, role, disabled state, tab order or accessible name.
      data-pendo={pendoName}
      onClick={onSelect}
      className="flex w-full items-center px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
    >
      {labelText}
    </button>
  )

  const toggleItem = (key, labelText, checked, onChange, sub) => (
    <button
      key={key}
      type="button"
      role="menuitemcheckbox"
      aria-checked={checked}
      /* Deliberately does NOT close the menu: these are settings, not actions, and
         a menu that shuts on every tick makes choosing two of them four clicks. */
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-2.5 px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        /* border-rc-seam, NOT border-rc-control — spec 117 §5 G1, closed 2026-09-09
           (spec 121 gap 3). This row is `hover:bg-gray-50 focus-visible:bg-gray-50`
           (#f9fafb), where #8b95a5 measures 2.896:1 — under SC 1.4.11's 3.0:1 exactly
           while the pointer or keyboard focus is on it, and an unchecked box's 1px
           border is the only thing saying a control is there. #6b7280 is 4.626:1.
           This export menu is Bar 1 of ALL 140 reports (ReportView + the specialized
           shell both render it), so it was the widest-reaching of the two remaining
           copies. It stays an inline box rather than <ControlCheckbox> because it is
           inside a <button role="menuitemcheckbox"> roved by PopoverMenu — see the
           twin in DataGrid.jsx. */
        className={`mt-[3px] grid h-3.5 w-3.5 flex-none place-items-center rounded-[3px] border ${checked ? 'border-rc-chip-text bg-rc-chip-text text-white' : 'border-rc-seam bg-white'}`}
      >
        {checked && (
          <svg viewBox="0 0 10 8" className="h-2 w-2.5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path d="M1 4l2.5 2.5L9 1" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="min-w-0">
        {labelText}
        {sub && <span className="mt-0.5 block text-[11px] text-rc-muted">{sub}</span>}
      </span>
    </button>
  )

  return (
    <PopoverMenu
      label="Export options"
      triggerLabel="Export"
      width={288}
      triggerContent={(
        <>
          <DownloadIcon className="h-4 w-4" />
          Export
          <ChevronDownIcon className="h-4 w-4 text-rc-muted" />
        </>
      )}
      data-testid="report-export-trigger"
      data-pendo="report-export-menu"
    >
      <>
          {exportFormats.length > 0 && (
            <>
              <p className="px-3.5 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">
                Download
              </p>
              {exportFormats.includes('xlsx') && formatItem('Excel (.xlsx)', handleExcel, 'report-export-xlsx')}
              {exportFormats.includes('csv') && formatItem('CSV', handleCsv, 'report-export-csv')}
              {/* CC-49594: PDF is client-only, so it is absent for bespoke reports with no grid view. */}
              {canScope && formatItem('PDF', handlePdf, 'report-export-pdf')}
              <div className="my-1.5 h-px bg-rc-line" />
            </>
          )}

          {/*
            spec 094 — Print is a SIXTH export action the tracking plan does not
            list. It downloads nothing, but it is a way a firm gets report
            figures out of the product, it lives in this menu, and leaving it
            uncounted would understate "what exports are they doing".
          */}
          {formatItem('Print', () => { emitExport('print', true); window.print() }, 'report-export-print')}

          {canScope && (
            <>
              <div className="my-1.5 h-px bg-rc-line" />
              {toggleItem('filters', 'Apply active column filters', honorFilters, setHonorFilters)}
              {toggleItem('columns', 'Only visible columns, current order', honorColumns, setHonorColumns)}
              {hasGroups && toggleItem('pdfsummary', 'PDF: summary only (group totals)', pdfSummaryOnly, setPdfSummaryOnly)}
              {/* CC-49844: persisted per user, so it survives the menu closing. */}
              {toggleItem('dataonly', 'Data only (no header block)', dataOnly, setDataOnly)}
              <div className="my-1.5 h-px bg-rc-line" />

              {/* CC-49354 #3: the grid is filtered but the scope boxes say full
                  dataset. Said where the decision is being made, rather than in a
                  warning below the fold that the user had already scrolled past. */}
              {gridFiltered && !honorFilters && (
                <p className="px-3.5 pb-1 text-[11px] text-rc-stale-text">
                  The grid is filtered — this exports all {rowCount.toLocaleString()} rows.
                </p>
              )}
              {/*
                CC-50021 / CC-50028: two testers independently filed the SAME bug —
                "the export doesn't match my screen" — against correct behaviour,
                because nothing on screen said which of the two the buttons made.
                The call was to make the default legible rather than change it. This
                line is that, and it updates as the boxes change. aria-live so a
                screen-reader user hears it: ticking a box is otherwise silent.
              */}
              <ExportScopeStatus scoped={scoped} honorFilters={honorFilters} rowCount={rowCount} getView={getView} />
            </>
          )}
      </>
    </PopoverMenu>
  )
}

/**
 * The menu's scope line, with the number of rows the file will hold (CC-54262,
 * spec 180). With "Apply active column filters" ticked the client writers export
 * the view's `visibleRows` (exportClientView.buildExportMatrix), so the count is
 * THEIRS — never `rowCount`, which is every row the run returned (a grid filtered
 * to 4 of 121 rows said "Exports what you see — 121 rows" and wrote 4). Unticked,
 * every row goes out and `rowCount` is the count.
 *
 * Its own component so the view is read only while the menu is OPEN: PopoverMenu
 * mounts its children only then, so a closed menu never builds a view. A count it
 * cannot read is not stated at all, never guessed — but a filter that leaves
 * nothing says "0 rows", because that IS what the file holds.
 */
function ExportScopeStatus({ scoped, honorFilters, rowCount, getView }) {
  let shown = rowCount || null
  if (scoped && honorFilters) {
    let rows = null
    try {
      rows = getView?.()?.visibleRows
    } catch (err) {
      console.error('Export row count unavailable', err)
    }
    shown = Array.isArray(rows) ? rows.length : null
  }
  // Spec 167's one row-count formatter: "1 row", "4 rows", "1,234 rows".
  const count = shown != null ? ` — ${fmtRowCount(shown)}` : ''
  return (
    <p role="status" aria-live="polite" className="px-3.5 pb-0.5 text-[11px] text-rc-muted">
      {scoped
        ? `Exports what you see${count}.`
        : `Exports the full report${count}, as the server produces it.`}
    </p>
  )
}

export default ReportExport
